import { ArchiveBlogQuery, OrderEnum, TaxonomyBlogCategoryQuery } from "../../types.graphql"
import { Link, useSearchParams } from "eddev/routing"
import { useBackground } from "@hooks/useAppState"
import { PageIntro } from "@features/content/PageIntro"
import { TabControl } from "@features/atoms/TabControl"
import { Chip } from "@features/atoms/Chip"
import dayjs from "dayjs"
import { Footer } from "@features/site/Footer"
import { useInfiniteBlogPostsSearch } from "@hooks/queries"
import { BlogPostTile } from "./BlogPostTile"
import { Select } from "@features/atoms/Select"
import { cn } from "@utils/tw"
import { motion } from "motion/react"

type ArchivePropsType = ArchiveBlogQuery | TaxonomyBlogCategoryQuery

function isTax(d: ArchiveBlogQuery | TaxonomyBlogCategoryQuery): d is TaxonomyBlogCategoryQuery {
  return (d as TaxonomyBlogCategoryQuery).blogCategory !== undefined
}

export const BlogArchive = (props: ArchivePropsType) => {
  const categories = props.blogCategories?.nodes || []
  const posts = (isTax(props) ? props.blogCategory?.blogPosts?.nodes : props.blogPosts?.nodes) || []
  const cat = isTax(props) ? props.blogCategory : undefined
  const tabItems = [
    {
      label: "Latest",
      value: "all",
      href: "/blog",
      onClick: (e: React.MouseEvent) => {
        e?.preventDefault()
        searchFilter.set("category", "")
      },
    },
    ...categories.map((category) => ({
      label: category.name!,
      value: category.name!,
      href: `/blog/category/${category.slug}`,
      onClick: (e: React.MouseEvent) => {
        e?.preventDefault()
        searchFilter.set("category", category.name!)
      },
    })),
  ]

  const [searchParams, searchFilter] = useSearchParams({
    search: "",
    category: cat?.name || "",
    tag: [] as string[],
    sort: OrderEnum.Desc,
  })

  const lookup = useInfiniteBlogPostsSearch({
    search: searchParams.search,
    category: searchParams.category,
    order: searchParams.sort,
  })

  const allPosts = (lookup.isLoading ? posts : lookup.data?.nodes) || []

  useBackground({
    scene: "idle",
    darken: 0.8,
  })

  return (
    <div>
      <div className="mb-12 flex flex-col items-center justify-center">
        <PageIntro
          layoutKey="blog-archive"
          spacingTop="page"
          eyebrow="Blog"
          title={
            <h1 className="flex flex-col">
              <span>Insights from </span>
              <span>the pigpen</span>
            </h1>
          }
        />
        <motion.div
          initial={{ opacity: 0, filter: "blur(20px)" }}
          animate={{ opacity: 1, filter: "none" }}
          exit={{ opacity: 0, filter: "blur(20px)" }}
          className="w-full md:w-auto"
        >
          <TabControl
            classNames={{ base: "mt-13 hidden md:!flex" }}
            items={tabItems}
            value={searchParams.category}
            defaultSelectFirstItem
          />

          <Select
            className={cn("md:hidden w-3/4 mx-auto mt-10")}
            value={searchParams.category}
            options={tabItems.map((item) => ({
              value: item.value,
              label: item.label,
            }))}
            onChange={(value) => {
              const item = tabItems.find((item) => item.value === value)
              if (item) {
                item.onClick?.(undefined as any) // Cast to MouseEvent, since Select doesn't provide it
              }
            }}
          />
        </motion.div>
        <div className="mt-10 w-grid mx-auto grid grid-cols-1 md:grid-cols-4 gap-5">
          {allPosts.map((post) => (
            <motion.div
              key={post.uri}
              initial={{ opacity: 0, filter: "blur(20px)" }}
              animate={{ opacity: 1, filter: "none" }}
              exit={{ opacity: 0, filter: "blur(20px)" }}
            >
              <BlogPostTile
                key={post.uri}
                title={post.title!}
                categories={post.info?.category?.map((c) => c?.name!) || []}
                date={post.date!}
                readTime={post.readTime!}
                link={post.uri!}
              />
            </motion.div>
          ))}
        </div>
      </div>
      <Footer />
    </div>
  )
}
